var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
/**
 * Select Package Modal Component
 * Modal with searchable package list for selecting an existing package
 */
import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { Tag, Collapse } from 'antd';
import Modal from '@fp-suite/core-ui/src/components/Modal';
import Input from '@fp-suite/core-ui/src/components/Input';
import Table from '@fp-suite/core-ui/src/components/Table';
import { usePackageManagement } from '../../../hooks/packing';
import { usePackingState } from '../../../hooks/packing';
export var SelectPackageModal = function (_a) {
    var visible = _a.visible, onClose = _a.onClose, onPackageSelect = _a.onPackageSelect;
    var t = useTranslation().t;
    var isMobileDevice = useMediaQuery({ query: '(max-device-width: 640px)' });
    var _b = useState(''), searchTerm = _b[0], setSearchTerm = _b[1];
    var _c = useState('all'), statusFilter = _c[0], setStatusFilter = _c[1];
    var getAllPackagesInfo = usePackageManagement().getAllPackagesInfo;
    var _d = usePackingState(), packageContentBySeqId = _d.packageContentBySeqId, boxTypes = _d.boxTypes;
    var allPackages = useMemo(function () { return getAllPackagesInfo(); }, [getAllPackagesInfo]);
    var filteredPackages = useMemo(function () {
        var packages = allPackages;
        if (statusFilter === 'open')
            packages = packages.filter(function (pkg) { return !pkg.isClosed; });
        else if (statusFilter === 'closed')
            packages = packages.filter(function (pkg) { return pkg.isClosed; });
        if (searchTerm) {
            packages = packages.filter(function (pkg) {
                return pkg.shipmentPackageSeqId.toLowerCase().includes(searchTerm.toLowerCase());
            });
        }
        return __spreadArray([], packages, true).sort(function (a, b) {
            if (a.isClosed === b.isClosed)
                return 0;
            return a.isClosed ? 1 : -1;
        });
    }, [allPackages, searchTerm, statusFilter]);
    var getBoxTypeName = useCallback(function (boxTypeId) {
        var _a;
        return (_a = boxTypes.find(function (boxType) { return boxType.key === boxTypeId; })) === null || _a === void 0 ? void 0 : _a.value;
    }, [boxTypes]);
    var handleSelect = useCallback(function (seqId) {
        onPackageSelect(seqId);
        setSearchTerm('');
        onClose();
    }, [onPackageSelect, onClose]);
    var productColumns = useMemo(function () { return [
        {
            title: t('Product'),
            dataIndex: 'productId',
            render: function (_, record) { return (_jsxs("span", __assign({ className: 'text-sm' }, { children: [record.productSku || record.productUpc || record.productId, record.productName ? ": " + record.productName : ''] }), void 0)); }
        },
        {
            title: t('Qty'),
            dataIndex: 'quantity',
            width: 80,
            render: function (qty) { return _jsx("span", __assign({ className: 'text-sm' }, { children: qty }), void 0); }
        }
    ]; }, [t]);
    return (_jsxs(Modal, __assign({ width: isMobileDevice ? '90%' : '60%', title: t('Select Package'), visible: visible, onCancel: function () {
            setSearchTerm('');
            onClose();
        } }, { children: [_jsx("div", __assign({ className: 'flex gap-2 mb-3' }, { children: ['all', 'open', 'closed'].map(function (filter) { return (_jsx("button", __assign({ type: 'button', className: "px-4 py-1.5 rounded-full text-sm font-semibold transition-all " + (statusFilter === filter
                        ? 'bg-primary text-white shadow-sm'
                        : 'bg-white text-gray-600 hover:bg-gray-200'), onClick: function () { return setStatusFilter(filter); } }, { children: t(filter.charAt(0).toUpperCase() + filter.slice(1)) }), filter)); }) }), void 0), _jsx("div", __assign({ className: 'mb-4' }, { children: _jsx(Input, { title: '', placeholder: t('Search by package ID...'), allowClear: true, value: searchTerm, onChange: function (e) { return setSearchTerm(e.target.value); } }, void 0) }), void 0), _jsx("div", __assign({ className: 'max-h-[60vh] overflow-y-auto' }, { children: filteredPackages.length === 0 ? (_jsx("div", __assign({ className: 'text-center text-gray-400 py-10 text-sm' }, { children: t('No packages available') }), void 0)) : (filteredPackages.map(function (pkg) {
                    var contents = packageContentBySeqId[pkg.shipmentPackageSeqId] || [];
                    return (_jsxs("div", __assign({ className: 'border border-gray-200 rounded-lg mb-3 overflow-hidden hover:border-blue-300 transition-colors' }, { children: [_jsxs("div", __assign({ className: 'flex items-center justify-between p-3.5 cursor-pointer hover:bg-gray-50 transition-colors', onClick: function () { return handleSelect(pkg.shipmentPackageSeqId); } }, { children: [_jsxs("div", __assign({ className: 'flex items-center gap-3' }, { children: [_jsxs("span", __assign({ className: 'font-bold text-base text-gray-800' }, { children: ["Package ", pkg.shipmentPackageSeqId] }), void 0), pkg.shipmentBoxTypeId && (_jsx("span", __assign({ className: 'text-sm text-gray-500' }, { children: getBoxTypeName(pkg.shipmentBoxTypeId) }), void 0))] }), void 0), _jsx(Tag, __assign({ color: pkg.isClosed ? 'green' : 'blue', style: { margin: 0, borderRadius: 6 } }, { children: pkg.isClosed ? t('Closed') : t('Open') }), void 0)] }), void 0), contents.length > 0 && (_jsx("div", __assign({ className: 'px-3 pb-3' }, { children: _jsx(Collapse, __assign({ ghost: true }, { children: _jsx(Collapse.Panel, __assign({ header: t('Products') + " (" + contents.length + ")" }, { children: _jsx(Table, { columns: productColumns, dataSource: contents, isPaginated: contents.length > 10 }, pkg.shipmentPackageSeqId) }), 'products') }), void 0) }), void 0))] }), pkg.shipmentPackageSeqId));
                })) }), void 0)] }), void 0));
};
export default SelectPackageModal;
